import type { UseQueryResult } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";

export const WithQuery = <T,>(props: {
	query: UseQueryResult<T | null>;
	render: (data: T | null) => ReactNode;
	showLoading?: boolean;
	data?: string;
}) => {
	const { data, isLoading, isFetching } = props.query;

	const loading = isLoading || isFetching;

	return (
		<WithSpinner loading={loading}>{props.render(data ?? null)}</WithSpinner>
	);
};

export const WithSpinner = ({
	loading,
	children,
}: {
	loading: boolean;
	children: React.ReactNode;
}) => {
	// Duration of the fade-out animation in milliseconds
	const fadeOutDuration = 500;

	// show is an internal state to control the visibility of the spinner.
	// - it is set to true immediately when loading becomes true
	// - it is set to false on delay when loading becomes false
	const [show, setShow] = useState(loading);
	// fadeout is used to trigger the fade-out animation
	// - it is set to true immediately when loading becomes false
	// - it is set to false when loading becomes true
	const [fadeout, setFadeOut] = useState(false);

	// Effect to handle visibility changes
	useEffect(() => {
		let timer: ReturnType<typeof setTimeout> | null = null;

		if (loading) {
			// When visible is true, show the spinner immediately
			setShow(true);
			// and cancel any fade-out animation
			setFadeOut(false);
		} else {
			// When visible is false, start the fade-out animation
			setFadeOut(true);
			// and hide the spinner after the animation duration (fadeOutDuration)
			timer = setTimeout(() => setShow(false), fadeOutDuration);
		}

		// Cleanup function to clear the timer if the component unmounts or visible changes
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [loading]);

	return (
		<>
			{show ? (
				<div className={`spinner-overlay ${fadeout ? "fade-out" : ""}`}>
					<div className={"loader"}></div>
					<div className={"loader-text"}>
						Please wait a few seconds while we fetch your data ...
					</div>
				</div>
			) : (
				children
			)}
		</>
	);
};
